import { FormControl, Grid, InputLabel, MenuItem, Select } from "@mui/material";
import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { ToggleDocumentDownloadPack } from "../../../../Component/Api/DocumentUpload/DocumentUploadHelper";
import { CommitteeService } from "../../../../Component/Api/EmploymentEquity/Committee/CommitteeService";
import { ConfirmationDialog } from "../../../../Component/Common/Components/ConfirmationDialog";
import { DataTable } from "../../../../Component/Common/Components/DataTable/DataTable";
import { MDBox } from "../../../../Component/Common/Components/MDBox";
import { ColumnSortOption } from "../../../../Component/Model/Dto/ColumnSortOption";
import { DocumentsForDownloadParameters } from "../../../../Component/Model/Dto/Document";
import { CommitteeMember, CommitteeSearchRequest } from "../../../../Component/Model/Dto/EmploymentEquity/Committee";
import { CommitteeType } from "../../../../Component/Model/Enum/EmploymentEquity";
import { EntriesPerPage } from "../../../../Component/Model/Table/Table";
import { defaultObject } from "../../../../Helpers/typescriptHelper";
import { ScorecardAtom } from "../../../../State/Scorecard.Atom";
import config from "../../../../config";
import { SupportingDocuments } from "../../../Common/SupportingDocuments/SupportingDocuments";
import { CommitteeMemberPersonModal } from "./CommitteeMemberPersonModal";
import { CommitteeMembersModal } from "./CommitteeMembersModal";
import { CommitteeMembersTableMapper } from "./CommitteeMembersTableColumn";
import { UserActivityService } from "../../../../Component/Api/UserActivity/UserActivityService";
import { UserActivityActionType } from "../../../../Component/Model/Enum/BEE123Enums";
import { CommitteeMembersSection } from "../../../../Component/Common/Constants/UserActivityTrackingSections";

interface IDropDownOptions {
    value: string,
    id: CommitteeType
}

export const CommitteeMembers = () => {
    const [previewPopup, setPreviewPopup] = React.useState<boolean>(false);
    const [documentsPopUp, setDocumentsPopUp] = React.useState<boolean>(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = React.useState<boolean>(false);
    const [membersData, setMembersData] = React.useState<CommitteeMember.DtoIn[]>(defaultObject);
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [selectedMemberId, setSelectedMemberId] = React.useState<string>("");
    const [selectedDocumentId, setSelectedDocumentId] = React.useState<string>("");
    const [columnSortOptions, setColumnSortOptions] = React.useState<ColumnSortOption[]>([]);
    const [selectedCommitteeType, setSelectedCommitteeType] = React.useState<CommitteeType>(CommitteeType.Forum);
    const [searchValue, setSearchValue] = React.useState<string>('');
    const scorecard = useRecoilValue(ScorecardAtom);

    const typeOptions: IDropDownOptions[] =
        [
            { value: "Forum", id: CommitteeType.Forum },
            { value: "Trade Union", id: CommitteeType.Trade_Union },
            { value: "All Employees", id: CommitteeType.All_Employees }
        ]

    const link = 'CommitteeMembers';
    const scorecardId = scorecard.scorecardId;
    const confirmDeleteMessage = "This action cannot be reversed. Are you sure you want to delete the member and all the associated supporting documents?"


    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
    }

    const handleCloseForm = () => {
        setPreviewPopup(false);
        setSelectedMemberId("");
    }

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectedMemberId("");
    }

    const handleEdit = (id: string) => {
        setSelectedMemberId(id);
        setPreviewPopup(true);
    }

    const handleConfirmDelete = (id: string) => {
        setSelectedMemberId(id);
        setShowConfirmationDialog(true);
    }

    const handleDelete = async () => {

        try {
            await CommitteeService.DeleteMember(selectedMemberId);
            toast.success("Member deleted succesfully")
            fetchData();

        } catch (error) {
            console.log(error)
        }
        handleCloseConfirmation();

    }

    const handleAttachedSD = (id: string) => {
        setSelectedDocumentId(id);
        setDocumentsPopUp(true);
    }

    const handleToggleDocumentPack = async (id: string) => {

        let added = membersData.find(objective => objective.id === id)?.documentsSelectedForDownload;
        setSelectedMemberId(id);
        if (added !== undefined) {

            const toggleDocumentsParamters: DocumentsForDownloadParameters = {
                entityId: id,
                scorecardId: scorecardId,
                link: link
            };

            await ToggleDocumentDownloadPack(toggleDocumentsParamters, added);
            fetchData();
        }

    }

    const onDocumentPopupClose = () => {
        setDocumentsPopUp(false);
        fetchData();
    }

    const membersSearchRequest: CommitteeSearchRequest = {
        pageNumber: pageNumber as number,
        pageSize: entriesPerPage.defaultValue as number,
        scorecardId: scorecardId,
        columnSortOptions: columnSortOptions,
        committeeType: selectedCommitteeType,
        searchValue: searchValue
    };

    const fetchData = async () => {
        setIsLoading(true);
        try {
            let searchResult = await CommitteeService.SearchMembers(membersSearchRequest);
            if (searchResult.data) {
                setMembersData(searchResult.data.results);
                setFilteredItemsCount(searchResult.data.filteredItemsCount);
                setTotalItemsCount(searchResult.data.totalItemsCount);

            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }

    };

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, columnSortOptions, selectedCommitteeType, searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        UserActivityService.createActivityLog({
            section: CommitteeMembersSection,
            actionType: UserActivityActionType.View,
            actionDescription: "View members",
            scorecardId: scorecardId,
            requestUrl: window.location.href
        });
    }, []);

    const handleAddMember = () => {
        setPreviewPopup(true)
    }
    const bulkActions = [
        {
            label: "Add New Member",
            action: handleAddMember
        }
    ]

    const committeeMembersTableData = CommitteeMembersTableMapper(membersData, handleEdit, handleConfirmDelete, handleAttachedSD, handleToggleDocumentPack);
    return (
        <>
            <Grid container>
                <Grid item xs={6} sm={6} md={6} lg={6}>
                    <MDBox mb={2}>
                        <FormControl fullWidth >
                            <InputLabel>{"Type"}</InputLabel>
                            <Select
                                value={selectedCommitteeType}
                                label={"Type"}
                                onChange={(e: any) => {
                                    setSelectedCommitteeType(e.target.value)
                                }}
                                sx={{
                                    padding: "0.75rem 0 !important",
                                    fontSize: "0.875rem"
                                }}
                                size={"medium"}
                            >
                                {typeOptions.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>{item.value}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </MDBox>
                </Grid>
            </Grid>

            <DataTable
                key={"CommitteeMembersTable"}
                table={committeeMembersTableData}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                isLoading={isLoading}
                bulkActions={bulkActions}
                manualSorting={true}
                columnSortingChanged={columnSortingChanged}
                canSearch={true}
                searchValueChanged={searchValueChanged}
            ></DataTable>

            {previewPopup && (config.enable_employment_equity_salary === 'true' ? (
                <CommitteeMembersModal
                    previewPopup={previewPopup}
                    id={selectedMemberId !== "" ? selectedMemberId : undefined}
                    link={link}
                    scorecardId={scorecardId}
                    handleCancel={handleCloseForm}
                    refreshTable={fetchData}
                    setMemberId={setSelectedMemberId}
                    committeeType={selectedCommitteeType} />
            ) : (
                <CommitteeMemberPersonModal
                    previewPopup={previewPopup}
                    id={selectedMemberId !== "" ? selectedMemberId : undefined}
                    link={link}
                    scorecardId={scorecardId}
                    handleCancel={handleCloseForm}
                    refreshTable={fetchData}
                    setMemberId={setSelectedMemberId}
                    committeeType={selectedCommitteeType} />
            ))}

            {documentsPopUp && <SupportingDocuments display={documentsPopUp} handleCancel={() => onDocumentPopupClose()} link={link} id={selectedDocumentId} 
                scorecardId={scorecardId} isEEModule={true} section={CommitteeMembersSection} />}

            <ConfirmationDialog
                showDialog={showConfirmationDialog}
                handleAccept={handleDelete}
                handleCancel={handleCloseConfirmation}
                heading={"Confirm Delete"}
                message={confirmDeleteMessage}
            />
        </>
    )
}